import React, { Dispatch } from "react";
import { SelectDropdown } from "@cadmakers-ui/core";
import { connect } from "react-redux";
import { ContextDropdown } from "cm-layout";
import { Redirect, RouteComponentProps, withRouter } from "react-router-dom";
import { SearchDropdown } from "cm-component-library/lib/components/SearchDropdown";
import { Application } from "../../App/Models/Application";
import { IAppState, LoadingState, appPaths, LoadingDisplay } from "../../Common";
import { setActiveApp, setActiveTopic } from "../../Startup/Actions/StartupActions";
import { Topic } from "../../Topic/Models/Topic";
import { AppSelectorGroup } from "../Constants/LayoutEnums";
import { FeedbackParent } from "../../Feedback/Constants/feedbackEnums";
import "../Styles/AppSelectorDropdown.scss";

type State = {
  shouldRedirect: boolean;
  redirectUrl?: string;
  prevLocation?: string;
  searchQuery: string;
};

type StateProps = {
  parentType: FeedbackParent;
  activeApp?: Application;
  activeTopic?: Topic;
  applications: Application[];
  appsLoadingState: LoadingState;
  topics: Topic[];
  topicsLoadingState: LoadingState;
};

type DispatchProps = {
  setActiveApp: (app: Application) => void;
  setActiveTopic: (topic: Topic) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  parentType: state.app.parentType,
  activeApp: state.app.activeApp,
  activeTopic: state.app.activeTopic,
  applications: state.app.applications,
  appsLoadingState: state.app.appsLoadingState,
  topics: state.app.topics,
  topicsLoadingState: state.app.topicsLoadingState
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  setActiveApp: (app: Application) => dispatch(setActiveApp(app)),
  setActiveTopic: (topic: Topic) => dispatch(setActiveTopic(topic))
});

class ContextSelectorDropdown extends React.Component<
  RouteComponentProps & DispatchProps & StateProps,
  State
> {
  constructor(props: RouteComponentProps & DispatchProps & StateProps) {
    super(props);

    this.state = {
      shouldRedirect: false,
      searchQuery: ""
    };

    this.onItemClick = this.onItemClick.bind(this);
  }

  static getDerivedStateFromProps = (
    nextProps: RouteComponentProps & DispatchProps & StateProps,
    prevState: State
  ) => {
    if (nextProps.location.pathname !== prevState.prevLocation) {
      return { prevLocation: nextProps.location.pathname };
    }

    return null;
  };

  componentDidUpdate = () => {
    const currentApp: Application | null = this.getActiveAppFromUrl(this.props.location.pathname);
    const currentTopic: Topic | null = this.getActiveTopicFromUrl(this.props.location.pathname);

    const activeAppRolePrefix = this.props.activeApp
      ? this.props.activeApp.rolePrefix
      : "notarealcode";
    const activeTopicCode = this.props.activeTopic ? this.props.activeTopic.code : "notarealcode";

    if (currentApp && currentApp.rolePrefix !== activeAppRolePrefix) {
      this.props.setActiveApp(currentApp);
    }

    if (currentTopic && currentTopic.code !== activeTopicCode) {
      this.props.setActiveTopic(currentTopic);
    }
  };

  getActiveAppFromUrl = (url: string): Application | null => {
    let currentApp: Application | null = null;

    this.props.applications.forEach(app => {
      if (url.includes(`/${app.rolePrefix}`)) {
        currentApp = app;
      }
    });

    return currentApp;
  };

  getActiveTopicFromUrl = (url: string): Topic | null => {
    let currentTopic: Topic | null = null;

    this.props.topics.forEach(topic => {
      if (url.includes(`/${topic.code}`)) {
        currentTopic = topic;
      }
    });

    return currentTopic;
  };

  onItemClick = (value: any) => {
    const { value: valueString } = value as any;

    this.setState(
      () => ({
        searchQuery: ""
      }),
      () => {
        if (this.props.applications.find(a => a.rolePrefix === valueString)) {
          this.handleAppItemClick(valueString);
        } else if (this.props.topics.find(t => t.code === valueString)) {
          this.handleTopicItemClick(valueString);
        } else {
          throw new Error(`Could not find dropdown item with value: ${valueString}`);
        }
      }
    );
  };

  handleAppItemClick = (value: string) => {
    const selectedApp = this.props.applications.find(app => app.rolePrefix === value);

    if (!selectedApp) {
      this.redirect(appPaths.notFound);
      return;
    }

    this.props.setActiveApp(selectedApp);

    const currentRoute = this.props.location.pathname;

    if (currentRoute.includes("/feedback/")) {
      // dont try converting the url because its a path to a specific feedback
      this.redirect(appPaths.app.root.replace(":id", selectedApp.rolePrefix));
      return;
    }

    if (this.props.parentType === FeedbackParent.Application) {
      const currentApp = this.getActiveAppFromUrl(currentRoute);

      if (currentApp) {
        this.redirect(currentRoute.replace(currentApp.rolePrefix, selectedApp.rolePrefix));
      } else if (!this.state.prevLocation || this.props.history.length === 1) {
        this.redirect(appPaths.app.root.replace(":id", selectedApp.rolePrefix));
      } else {
        const prevApp = this.getActiveAppFromUrl(this.state.prevLocation);

        if (prevApp) {
          this.redirect(
            this.state.prevLocation.replace(prevApp.rolePrefix, selectedApp.rolePrefix)
          );
        } else {
          this.redirect(appPaths.app.root.replace(":id", selectedApp.rolePrefix));
        }
      }
    } else if (this.props.parentType === FeedbackParent.Topic) {
      const currentTopic = this.getActiveTopicFromUrl(currentRoute);

      if (currentTopic) {
        this.redirect(currentRoute.replace(`topic/${currentTopic.code}`, selectedApp.rolePrefix));
      } else if (!this.state.prevLocation || this.props.history.length === 1) {
        this.redirect(appPaths.app.root.replace(":id", selectedApp.rolePrefix));
      } else {
        const prevTopic = this.getActiveTopicFromUrl(this.state.prevLocation);

        if (prevTopic) {
          this.redirect(
            this.state.prevLocation.replace(`topic/${prevTopic.code}`, selectedApp.rolePrefix)
          );
        } else {
          this.redirect(appPaths.app.root.replace(":id", selectedApp.rolePrefix));
        }
      }
    } else {
      this.redirect(appPaths.notFound);
    }
  };

  handleTopicItemClick = (value: string) => {
    const selectedTopic = this.props.topics.find(topic => topic.code === value);

    if (!selectedTopic) {
      this.redirect(appPaths.notFound);
      return;
    }

    this.props.setActiveTopic(selectedTopic);

    const currentRoute = this.props.location.pathname;

    if (currentRoute.includes("/feedback/featurerequests")) {
      // dont try converting the url because its a path to a specific feedback
      this.redirect(appPaths.topic.featureRequests.replace(":topicCode", selectedTopic.code));
      return;
    }

    if (this.props.parentType === FeedbackParent.Application) {
      const currentApp = this.getActiveAppFromUrl(currentRoute);

      let newRoute = currentRoute;

      if (currentApp !== null) {
        newRoute = currentRoute.replace(currentApp.rolePrefix, `topic/${selectedTopic.code}`);
      }

      if (currentRoute !== newRoute) {
        this.redirect(newRoute);
      } else {
        this.redirect(appPaths.topic.featureRequests.replace(":topicCode", selectedTopic.code));
      }
    } else if (this.props.parentType === FeedbackParent.Topic) {
      const currentTopic = this.getActiveTopicFromUrl(currentRoute);

      let newRoute = currentRoute;

      if (currentTopic !== null) {
        newRoute = currentRoute.replace(currentTopic.code, selectedTopic.code);
      }

      if (currentRoute !== newRoute) {
        this.redirect(newRoute);
      } else {
        this.redirect(appPaths.topic.featureRequests.replace(":topicCode", selectedTopic.code));
      }
    } else {
      this.redirect(appPaths.notFound);
    }
  };

  redirect = (url: string) => {
    this.setState({
      shouldRedirect: true,
      redirectUrl: url
    });
  };

  getInitialsForApp = (rolePrefix: string) => {
    switch (rolePrefix) {
      case "IM":
        return "T";
      case "PM_":
        return "PM";
      case "CMK":
        return "K";
      case "EXE":
        return "E";
      case "THV2":
        return "T2";
      case "WIKI":
        return "W";
      case "CMB":
        return "B";
      case "CMBL":
        return "B";
      case "CMF":
        return "F";
      case "TBX":
        return "TB";
      case "CMV":
        return "V";
      default:
        return rolePrefix;
    }
  };

  getInitialsForTopic = (topicName: string) => {
    const splitTopicName = topicName.split(" ");
    const firstChar = splitTopicName[0].charAt(0).toUpperCase();
    const secondChar = splitTopicName[1] ? splitTopicName[1].charAt(0).toUpperCase() : "";

    return `${firstChar}${secondChar}`;
  };

  getApplicationItems = () => {
    return this.props.applications
      .filter(
        a =>
          !this.state.searchQuery ||
          a.name.toLowerCase().includes(this.state.searchQuery.toLowerCase())
      )
      .map((app: Application) => {
        return {
          name: app.name,
          initials: this.getInitialsForApp(app.rolePrefix),
          value: app.rolePrefix
        };
      });
  };

  getTopicItems = () => {
    return this.props.topics
      .filter(
        t =>
          !this.state.searchQuery ||
          t.name.toLowerCase().includes(this.state.searchQuery.toLowerCase())
      )
      .map(topic => {
        return {
          name: topic.name,
          initials: this.getInitialsForTopic(topic.name),
          value: topic.code
        };
      });
  };

  getDropdownValue = () => {
    switch (this.props.parentType) {
      case FeedbackParent.Application:
        return {
          name: (this.props.activeApp as Application).name,
          initials: this.getInitialsForApp((this.props.activeApp as Application).rolePrefix),
          value: (this.props.activeApp as Application).rolePrefix
        };
      case FeedbackParent.Topic:
        return {
          name: (this.props.activeTopic as Topic).name,
          initials: this.getInitialsForTopic((this.props.activeTopic as Topic).name),
          value: (this.props.activeTopic as Topic).code
        };
      default:
        throw new Error(`Invalid parent type "${this.props.parentType}"`);
    }
  };

  onChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    this.setState(() => ({
      searchQuery: value
    }));
  };

  render() {
    if (!this.props.appsLoadingState.isLoaded || !this.props.topicsLoadingState.isLoaded) {
      return <LoadingDisplay />;
    }

    const isAppScope = !!this.getActiveAppFromUrl(this.props.location.pathname);

    const items = [
      ...(isAppScope ? this.getApplicationItems() : []),
      ...(!isAppScope ? this.getTopicItems() : [])
    ];

    return (
      <>
        {this.state.shouldRedirect && !!this.state.redirectUrl ? (
          <Redirect to={this.state.redirectUrl} push />
        ) : (
          ""
        )}
        <ContextDropdown
          items={items}
          value={this.getDropdownValue()}
          onItemSelected={this.onItemClick}
          searchQuery={this.state.searchQuery}
          onSearchQueryChange={this.onChangeSearchQuery}
        />
      </>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ContextSelectorDropdown));
